import { useQuery, useInfiniteQuery, useMutation, useTransport, createConnectQueryKey, createQueryOptions } from '@connectrpc/connect-query'
import { keepPreviousData } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { useQueryClient, type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { useMemo } from 'react'
import type { Transport } from '@connectrpc/connect'

import { listInquiries, getInquiry, updateInquiry, createInquiry, generatePortalLink, sharePublicInquiryByEmail, moveInquiryToEvents, acceptQuoteAsAdmin, getQuote, getQuoteLitesByIds, listJobActivity, createInquiryEvent, updateInquiryEvent, deleteInquiryEvent, listQuotes, getQuoteDetail, listEvents, getDashboardSummary, archiveInquiry, unarchiveInquiry, listJobs, getJob, getInquiryDriveTime, listJobEvents } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListInquiriesRequestSchema, GetInquiryRequestSchema, UpdateInquiryRequestSchema, GetInquiryResponseSchema, MoveInquiryToEventsRequestSchema, ListInquiriesResponseSchema, AcceptQuoteAsAdminRequestSchema, GetInquiryDriveTimeRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { GetJobResponseSchema, ListJobsResponseSchema, type GetJobResponse, type ListJobsResponse } from '@/gen/cortado/v1/jobs_pb'
import type { GetInquiryResponse, ListInquiriesResponse } from '@/gen/cortado/v1/cortado_pb'
import { CreateInquiryEventRequestSchema, UpdateInquiryEventRequestSchema, DeleteInquiryEventRequestSchema, ArchiveInquiryRequestSchema, UnarchiveInquiryRequestSchema } from '@/gen/cortado/v1/inquiries_pb'
import type { InquiryLite } from '@/gen/cortado/v1/inquiries_pb'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { type InquiryStageEnum, type InquiryRejectReasonEnum } from '@/gen/cortado/v1/settings_pb'
import { organizationStore } from './organization.store'
import { authStore } from './auth.store'
import { dateToTimestamp } from '@/lib/date-utils'
import { deriveSource } from '@/lib/source-utils'
import { connectTransport } from '@/lib/connect-transport'

export interface ListInquiriesQueryOptions {
  organizationId: string
  contactId?: string
  stages?: InquiryStageEnum[]
  eventTypes?: string[]
  since?: Date
  createdBefore?: Date
  eventDateStart?: Date
  eventDateEnd?: Date
  limit?: number
  offset?: number
  /** Server-side text search over contact name / company. */
  search?: string
  /** Exact derived source-channel display labels. */
  sourceChannels?: string[]
  /** Filter by assigned user IDs (matches any). */
  assignedTo?: string[]
  /** When true, unassigned leads are included in the filter. */
  includeUnassigned?: boolean
  /** When true, archived inquiries are returned alongside active ones. */
  includeArchived?: boolean
}

export function createListInquiriesRequest(options: ListInquiriesQueryOptions) {
  return create(ListInquiriesRequestSchema, {
    organizationId: options.organizationId,
    contactId: options.contactId,
    eventDateStart: options.eventDateStart ? dateToTimestamp(options.eventDateStart) : undefined,
    eventDateEnd: options.eventDateEnd ? dateToTimestamp(options.eventDateEnd) : undefined,
    stages: options.stages || [],
    eventTypeKeys: options.eventTypes || [],
    since: options.since ? dateToTimestamp(options.since) : undefined,
    createdBefore: options.createdBefore ? dateToTimestamp(options.createdBefore) : undefined,
    limit: options.limit,
    offset: options.offset,
    search: options.search,
    sourceChannels: options.sourceChannels || [],
    assignedTo: options.assignedTo || [],
    includeUnassigned: options.includeUnassigned,
    includeArchived: options.includeArchived,
  })
}

export function listInquiriesQueryOptions(
  options: ListInquiriesQueryOptions,
  transport: Transport = connectTransport,
) {
  return createQueryOptions(listInquiries, createListInquiriesRequest(options), { transport })
}

export function getInquiryQueryOptions(inquiryId: string, transport: Transport = connectTransport) {
  return createQueryOptions(getInquiry, create(GetInquiryRequestSchema, { id: inquiryId }), { transport })
}

/**
 * Hook to fetch all inquiries for an organization
 * 
 * This hook:
 * - Fetches all inquiries for the selected organization
 * - Uses lightweight InquiryLite format optimized for list views
 * - Supports filtering by stages, dates, and pagination
 * - Includes quote totals for each inquiry
 * - Returns lightweight inquiry data with contact summary
 * 
 * @param options - Optional filters for stages, dates, and pagination
 * @returns Query result with inquiries array (InquiryLite[]), loading state, and error
 */
export function useLeadsList(options?: {
  contactId?: string
  stages?: InquiryStageEnum[]
  eventTypes?: string[]
  since?: Date
  createdBefore?: Date
  eventDateStart?: Date
  eventDateEnd?: Date
  limit?: number
  offset?: number
  enabled?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  // Memoize request
  const request = useMemo(() => {
    return createListInquiriesRequest({
      organizationId: selectedOrganizationId || '',
      contactId: options?.contactId,
      stages: options?.stages || [],
      eventTypes: options?.eventTypes || [],
      since: options?.since,
      createdBefore: options?.createdBefore,
      eventDateStart: options?.eventDateStart,
      eventDateEnd: options?.eventDateEnd,
      limit: options?.limit,
      offset: options?.offset,
    })
  }, [selectedOrganizationId, options?.contactId, options?.stages, options?.eventTypes, options?.since, options?.createdBefore, options?.eventDateStart, options?.eventDateEnd, options?.limit, options?.offset])

  const query = useQuery(
    listInquiries,
    request,
    {
      enabled: !!selectedOrganizationId && (options?.enabled ?? true),
      select: (data) => data?.inquiries || [],
      placeholderData: keepPreviousData,
    }
  )

  return {
    inquiries: query.data || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Count-only variant of {@link useLeadsList}.
 *
 * Returns the server `COUNT(*)` of inquiries matching the given filters WITHOUT
 * fetching the rows (`limit: 1`). The server's `totalCount` is a real count that
 * respects all server-side filters, so this stays correct and O(1) under the
 * 100-row page cap — unlike counting a fetched `inquiries` array.
 */
export function useLeadsCount(options?: {
  contactId?: string
  stages?: InquiryStageEnum[]
  eventTypes?: string[]
  since?: Date
  createdBefore?: Date
  eventDateStart?: Date
  eventDateEnd?: Date
  /** Server-side text search over contact name / company. */
  search?: string
  sourceChannels?: string[]
  assignedTo?: string[]
  includeUnassigned?: boolean
  includeArchived?: boolean
  enabled?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    return createListInquiriesRequest({
      organizationId: selectedOrganizationId || '',
      contactId: options?.contactId,
      stages: options?.stages || [],
      eventTypes: options?.eventTypes || [],
      since: options?.since,
      createdBefore: options?.createdBefore,
      eventDateStart: options?.eventDateStart,
      eventDateEnd: options?.eventDateEnd,
      search: options?.search,
      sourceChannels: options?.sourceChannels || [],
      assignedTo: options?.assignedTo || [],
      includeUnassigned: options?.includeUnassigned,
      includeArchived: options?.includeArchived,
      limit: 1,
    })
  }, [selectedOrganizationId, options?.contactId, options?.stages, options?.eventTypes, options?.since, options?.createdBefore, options?.eventDateStart, options?.eventDateEnd, options?.search, options?.sourceChannels, options?.assignedTo, options?.includeUnassigned, options?.includeArchived])

  const query = useQuery(
    listInquiries,
    request,
    {
      enabled: !!selectedOrganizationId && (options?.enabled ?? true),
      select: (data) => data?.totalCount ?? 0,
      placeholderData: keepPreviousData,
    }
  )

  return {
    count: query.data ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || query.data !== undefined,
  }
}

/** Server-enforced max page size for ListInquiries (see backend handler clamp). */
const LEADS_PAGE_SIZE = 100

/**
 * Paginated variant of {@link useLeadsList} for the Leads page.
 *
 * Fetches inquiries in offset-based pages of {@link LEADS_PAGE_SIZE} and exposes
 * `fetchNextPage`/`hasMore` for a "Load more" affordance. The backend orders
 * inquiries newest-first, so older inquiries appear on later pages. Other
 * consumers that only need a flat list should keep using `useLeadsList`.
 */
export function useInfiniteLeadsList(options?: {
  contactId?: string
  stages?: InquiryStageEnum[]
  eventTypes?: string[]
  since?: Date
  createdBefore?: Date
  eventDateStart?: Date
  eventDateEnd?: Date
  search?: string
  sourceChannels?: string[]
  includeArchived?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  // Init shape (not a created message) so `offset` is a definite number, which
  // connect-query's useInfiniteQuery requires for the page-param field.
  const input = useMemo(() => ({
    organizationId: selectedOrganizationId || '',
    contactId: options?.contactId,
    stages: options?.stages || [],
    eventTypeKeys: options?.eventTypes || [],
    since: options?.since ? dateToTimestamp(options.since) : undefined,
    createdBefore: options?.createdBefore ? dateToTimestamp(options.createdBefore) : undefined,
    eventDateStart: options?.eventDateStart ? dateToTimestamp(options.eventDateStart) : undefined,
    eventDateEnd: options?.eventDateEnd ? dateToTimestamp(options.eventDateEnd) : undefined,
    search: options?.search,
    sourceChannels: options?.sourceChannels || [],
    includeArchived: options?.includeArchived,
    limit: LEADS_PAGE_SIZE,
    offset: 0,
  }), [selectedOrganizationId, options?.contactId, options?.stages, options?.eventTypes, options?.since, options?.createdBefore, options?.eventDateStart, options?.eventDateEnd, options?.search, options?.sourceChannels, options?.includeArchived])

  const query = useInfiniteQuery(listInquiries, input, {
    enabled: !!selectedOrganizationId,
    pageParamKey: 'offset',
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.inquiries.length, 0)
      return loaded < lastPage.totalCount ? loaded : undefined
    },
    placeholderData: keepPreviousData,
  })

  // Dedupe by id: optimistic cross-cache moves shift offset boundaries, so a
  // later page can re-deliver a row from an earlier page.
  const inquiries = useMemo(() => {
    const seen = new Set<string>()
    const rows: InquiryLite[] = []
    for (const page of query.data?.pages ?? []) {
      for (const inquiry of page.inquiries) {
        if (seen.has(inquiry.id)) continue
        seen.add(inquiry.id)
        rows.push(inquiry)
      }
    }
    return rows
  }, [query.data])
  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0

  return {
    inquiries,
    totalCount,
    hasMore: inquiries.length < totalCount,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/** Page size for the per-stage kanban column queries on the leads page. */
export const STAGE_LEADS_PAGE_SIZE = 25

/**
 * Server-offset for the next per-stage page, capping each page's contribution
 * at {@link STAGE_LEADS_PAGE_SIZE}. Optimistic moves
 * ({@link moveInquiryStageInListCaches}) prepend a client-fabricated row into a
 * destination page, so a full page can hold PAGE_SIZE+1 rows — a raw sum would
 * then over-advance the offset and silently skip the server row at the old
 * boundary until a full refetch. Capping keeps the offset aligned with rows the
 * server actually delivered; any re-delivered row is deduped by id downstream.
 */
export function stageLeadsLoadedCount(pages: Array<Pick<ListInquiriesResponse, 'inquiries'>>): number {
  return pages.reduce(
    (sum, page) => sum + Math.min(page.inquiries.length, STAGE_LEADS_PAGE_SIZE),
    0,
  )
}

/**
 * Filters shared by every per-stage leads query — {@link ListInquiriesQueryOptions}
 * minus `stages` (each stage column pins its own single-stage filter).
 */
export interface StageLeadsFilterOptions {
  eventTypes?: string[]
  since?: Date
  createdBefore?: Date
  eventDateStart?: Date
  eventDateEnd?: Date
  search?: string
  sourceChannels?: string[]
  assignedTo?: string[]
  includeUnassigned?: boolean
  /**
   * When true, archived inquiries are returned alongside active ones. Left
   * `undefined` (rather than `false`) when off so the query key is unchanged
   * for callers that never ask for archived rows.
   */
  includeArchived?: boolean
}

/**
 * Per-stage variant of {@link useInfiniteLeadsList} for the leads kanban.
 *
 * Each stage column runs its own infinite query (`stages: [stage]`) in pages of
 * {@link STAGE_LEADS_PAGE_SIZE}, so columns load and paginate independently.
 */
export function useStageLeadsList(
  stage: InquiryStageEnum,
  options?: StageLeadsFilterOptions & { enabled?: boolean },
) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  // Init shape (not a created message) so `offset` is a definite number, which
  // connect-query's useInfiniteQuery requires for the page-param field.
  const input = useMemo(() => ({
    organizationId: selectedOrganizationId || '',
    stages: [stage],
    eventTypeKeys: options?.eventTypes || [],
    since: options?.since ? dateToTimestamp(options.since) : undefined,
    createdBefore: options?.createdBefore ? dateToTimestamp(options.createdBefore) : undefined,
    eventDateStart: options?.eventDateStart ? dateToTimestamp(options.eventDateStart) : undefined,
    eventDateEnd: options?.eventDateEnd ? dateToTimestamp(options.eventDateEnd) : undefined,
    search: options?.search,
    sourceChannels: options?.sourceChannels || [],
    assignedTo: options?.assignedTo || [],
    includeUnassigned: options?.includeUnassigned,
    includeArchived: options?.includeArchived,
    limit: STAGE_LEADS_PAGE_SIZE,
    offset: 0,
  }), [selectedOrganizationId, stage, options?.eventTypes, options?.since, options?.createdBefore, options?.eventDateStart, options?.eventDateEnd, options?.search, options?.sourceChannels, options?.assignedTo, options?.includeUnassigned, options?.includeArchived])

  const query = useInfiniteQuery(listInquiries, input, {
    enabled: !!selectedOrganizationId && (options?.enabled ?? true),
    pageParamKey: 'offset',
    getNextPageParam: (lastPage, allPages) => {
      const loaded = stageLeadsLoadedCount(allPages)
      return loaded < lastPage.totalCount ? loaded : undefined
    },
    placeholderData: keepPreviousData,
  })

  // Flatten pages, deduping by id: optimistic cross-cache moves shift offset
  // boundaries, so a later page can re-deliver a row from an earlier page.
  const inquiries = useMemo(() => {
    const seen = new Set<string>()
    const rows: InquiryLite[] = []
    for (const page of query.data?.pages ?? []) {
      for (const inquiry of page.inquiries) {
        if (seen.has(inquiry.id)) continue
        seen.add(inquiry.id)
        rows.push(inquiry)
      }
    }
    return rows
  }, [query.data])

  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0

  return {
    inquiries,
    totalCount,
    hasMore: inquiries.length < totalCount,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

type InquiryListCache = ListInquiriesResponse | InfiniteData<ListInquiriesResponse>

/**
 * Apply a stage change for a single inquiry IN PLACE to every cached inquiry
 * list, handling both the flat `ListInquiriesResponse` shape (`useLeadsList`)
 * and the paginated `InfiniteData` shape (`useInfiniteLeadsList`). Fallback
 * delegate for {@link moveInquiryStageInListCaches}.
 */
function setInquiryStageInListCaches(
  queryClient: QueryClient,
  listQueryKey: QueryKey,
  inquiryId: string,
  stage: InquiryStageEnum,
) {
  const caches = queryClient.getQueriesData<InquiryListCache>({ queryKey: listQueryKey })
  caches.forEach(([queryKey, data]) => {
    if (!data) return

    // Paginated (infinite) cache: patch the inquiry within whichever page holds it.
    if ('pages' in data) {
      queryClient.setQueryData(queryKey, {
        ...data,
        pages: data.pages.map((page) =>
          create(ListInquiriesResponseSchema, {
            inquiries: page.inquiries.map((inquiry) =>
              inquiry.id === inquiryId ? { ...inquiry, stage } : inquiry,
            ),
            totalCount: page.totalCount,
          }),
        ),
      })
      return
    }

    // Flat cache.
    if (!data.inquiries) return
    queryClient.setQueryData(
      queryKey,
      create(ListInquiriesResponseSchema, {
        inquiries: data.inquiries.map((inquiry) =>
          inquiry.id === inquiryId ? { ...inquiry, stage } : inquiry,
        ),
        totalCount: data.totalCount,
      }),
    )
  })
}

/**
 * Move a single inquiry between stage-scoped inquiry list caches, keeping every
 * cached list (and its `totalCount`) consistent with the stage change. Used for
 * optimistic kanban stage updates and server-confirmed reconciliation.
 *
 * Each cache is classified by every filter in its query-key input.
 * connect-query keys are
 * `["connect-query", { transport, serviceName, methodName, input, cardinality }]`
 * and `input` only includes SET fields — so a per-stage cache has
 * `input.stages = [n]`, while a cache with no stages filter has no `stages` key
 * and is treated as containing every stage.
 *
 * Offset-based pagination means removing/inserting rows shifts page
 *    boundaries, so a later fetch can re-deliver a row — consumers dedupe by id.
 */
export function moveInquiryStageInListCaches(
  queryClient: QueryClient,
  listQueryKey: QueryKey,
  inquiryId: string,
  newStage: InquiryStageEnum,
): void {
  const caches = queryClient.getQueriesData<InquiryListCache>({ queryKey: listQueryKey })

  // Pass 1 — locate the full row (and its current stage) in any cache.
  let movedRow: InquiryLite | undefined
  for (const [, data] of caches) {
    if (!data) continue
    const pages = 'pages' in data ? data.pages : [data]
    for (const page of pages) {
      const row = page.inquiries?.find((inquiry) => inquiry.id === inquiryId)
      if (row) {
        movedRow = row
        break
      }
    }
    if (movedRow) break
  }

  // Fall back to the legacy in-place patch when the row isn't cached anywhere,
  // or when the stage already matches (idempotent onSuccess reconcile after the
  // optimistic move already ran).
  if (!movedRow || movedRow.stage === newStage) {
    setInquiryStageInListCaches(queryClient, listQueryKey, inquiryId, newStage)
    return
  }

  const row = movedRow

  // Pass 2 — classify each cache and apply the move.
  caches.forEach(([queryKey, data]) => {
    if (!data) return

    const meta = queryKey[1] as { input?: Record<string, unknown> | 'skipped' } | undefined
    const input = meta?.input
    const cacheInput = input && input !== 'skipped' ? input : undefined
    const hasFrom = inquiryMatchesListInput(row, cacheInput)
    const hasTo = inquiryMatchesListInput({ ...row, stage: newStage }, cacheInput)

    if (!hasFrom && !hasTo) return

    if (hasFrom && hasTo) {
      // Multi-stage or unfiltered cache (calendar, entity-linker, search):
      // in-place stage patch where the row is present; totalCount unchanged.
      if ('pages' in data) {
        queryClient.setQueryData(queryKey, {
          ...data,
          pages: data.pages.map((page) =>
            create(ListInquiriesResponseSchema, {
              inquiries: page.inquiries.map((inquiry) =>
                inquiry.id === inquiryId ? { ...inquiry, stage: newStage } : inquiry,
              ),
              totalCount: page.totalCount,
            }),
          ),
        })
        return
      }
      queryClient.setQueryData(
        queryKey,
        create(ListInquiriesResponseSchema, {
          inquiries: (data.inquiries ?? []).map((inquiry) =>
            inquiry.id === inquiryId ? { ...inquiry, stage: newStage } : inquiry,
          ),
          totalCount: data.totalCount,
        }),
      )
      return
    }

    if (hasFrom) {
      // Source-stage cache (including limit-1 count caches): drop the row and
      // decrement totalCount on EVERY page — the hooks read
      // `pages.at(-1)?.totalCount`, and count caches hold ≤1 row, so the count
      // must decrement even when the row isn't physically present.
      if ('pages' in data) {
        queryClient.setQueryData(queryKey, {
          ...data,
          pages: data.pages.map((page) =>
            create(ListInquiriesResponseSchema, {
              inquiries: page.inquiries.filter((inquiry) => inquiry.id !== inquiryId),
              totalCount: Math.max(0, page.totalCount - 1),
            }),
          ),
        })
        return
      }
      queryClient.setQueryData(
        queryKey,
        create(ListInquiriesResponseSchema, {
          inquiries: (data.inquiries ?? []).filter((inquiry) => inquiry.id !== inquiryId),
          totalCount: Math.max(0, data.totalCount - 1),
        }),
      )
      return
    }

    // Destination-stage cache: bump totalCount on every page. Prepend the moved
    // row to the first page of infinite caches only — flat caches here are
    // limit-bounded count queries, so NEVER insert rows into them.
    if ('pages' in data) {
      const alreadyPresent = data.pages.some((page) =>
        page.inquiries.some((inquiry) => inquiry.id === inquiryId),
      )
      queryClient.setQueryData(queryKey, {
        ...data,
        pages: data.pages.map((page, pageIndex) =>
          create(ListInquiriesResponseSchema, {
            inquiries: pageIndex === 0 && !alreadyPresent
              ? [{ ...row, stage: newStage }, ...page.inquiries]
              : page.inquiries,
            totalCount: page.totalCount + 1,
          }),
        ),
      })
      return
    }
    queryClient.setQueryData(
      queryKey,
      create(ListInquiriesResponseSchema, {
        inquiries: data.inquiries ?? [],
        totalCount: data.totalCount + 1,
      }),
    )
  })
}

type TimestampInput = { seconds?: bigint | number; nanos?: number }

function timestampMillis(value: unknown): number | undefined {
  if (!value || typeof value !== 'object') return undefined
  const timestamp = value as TimestampInput
  if (timestamp.seconds === undefined) return undefined
  return Number(timestamp.seconds) * 1000 + Math.floor((timestamp.nanos ?? 0) / 1_000_000)
}

export function inquiryMatchesListInput(
  inquiry: InquiryLite,
  input?: Record<string, unknown>,
): boolean {
  if (!input) return true

  if (typeof input.organizationId === 'string' && input.organizationId && inquiry.organizationId !== input.organizationId) return false
  if (typeof input.contactId === 'string' && input.contactId && inquiry.contactId !== input.contactId) return false

  const stages = Array.isArray(input.stages) ? input.stages as number[] : []
  if (stages.length > 0 && !stages.includes(inquiry.stage)) return false

  const eventTypes = Array.isArray(input.eventTypeKeys) ? input.eventTypeKeys as string[] : []
  if (eventTypes.length > 0 && (!inquiry.eventTypeKey || !eventTypes.includes(inquiry.eventTypeKey))) return false

  const createdAt = timestampMillis(inquiry.createdAt)
  const since = timestampMillis(input.since)
  if (since !== undefined && (createdAt === undefined || createdAt < since)) return false
  const createdBefore = timestampMillis(input.createdBefore)
  if (createdBefore !== undefined && (createdAt === undefined || createdAt > createdBefore)) return false

  const eventDateStart = timestampMillis(input.eventDateStart)
  const lastEventDate = timestampMillis(inquiry.lastEventDate)
  if (eventDateStart !== undefined && (lastEventDate === undefined || lastEventDate < eventDateStart)) return false
  const eventDateEnd = timestampMillis(input.eventDateEnd)
  const firstEventDate = timestampMillis(inquiry.firstEventDate)
  if (eventDateEnd !== undefined && (firstEventDate === undefined || firstEventDate > eventDateEnd)) return false

  const search = typeof input.search === 'string' ? input.search.toLowerCase() : ''
  if (search) {
    const firstName = inquiry.contactFirstName ?? ''
    const lastName = inquiry.contactLastName ?? ''
    const companyName = inquiry.contactCompanyName ?? ''
    const values = [firstName, lastName, `${firstName} ${lastName}`, companyName]
    if (!values.some((value) => value.toLowerCase().includes(search))) return false
  }

  const sourceChannels = Array.isArray(input.sourceChannels) ? input.sourceChannels as string[] : []
  if (sourceChannels.length > 0 && !sourceChannels.includes(deriveSource(inquiry.source).channel)) return false

  const assignedTo = Array.isArray(input.assignedTo) ? input.assignedTo as string[] : []
  const includeUnassigned = input.includeUnassigned === true
  if (assignedTo.length > 0 || includeUnassigned) {
    const inquiryAssignee = inquiry.assignedTo ?? ''
    const matchesAssignee = assignedTo.length > 0 && assignedTo.includes(inquiryAssignee)
    const matchesUnassigned = includeUnassigned && !inquiryAssignee
    if (!matchesAssignee && !matchesUnassigned) return false
  }

  return true
}

type JobsListCache = ListJobsResponse | InfiniteData<ListJobsResponse>

/**
 * Patch `assigned_to` IN PLACE on every cached `ListJobs` response's `JobLite`
 * rows for the given inquiry — a `JobLite.assigned_to` is always derived from
 * its inquiry's `assigned_to`, so a Pipeline table/board row must reflect an
 * inquiry assignee change. Matches both the flat `ListJobsResponse` shape and
 * the paginated `InfiniteData` shape `useInfinitePipelineJobs`/
 * `useInfinitePipelineBoardColumn` use. Used for the optimistic update and its
 * error rollback in {@link useUpdateInquiry}.
 */
function patchJobAssigneeInListCaches(
  queryClient: QueryClient,
  listQueryKey: QueryKey,
  inquiryId: string,
  assignedTo: string,
): void {
  const patchPage = (page: ListJobsResponse) =>
    create(ListJobsResponseSchema, {
      jobs: page.jobs.map((job) =>
        job.inquiryId === inquiryId ? { ...job, assignedTo: assignedTo || undefined } : job,
      ),
      totalCount: page.totalCount,
    })

  const caches = queryClient.getQueriesData<JobsListCache>({ queryKey: listQueryKey })
  caches.forEach(([queryKey, data]) => {
    if (!data) return

    if ('pages' in data) {
      queryClient.setQueryData(queryKey, { ...data, pages: data.pages.map(patchPage) })
      return
    }

    if (!data.jobs) return
    queryClient.setQueryData(queryKey, patchPage(data))
  })
}

/**
 * Patch `assigned_to` on every cached `GetJob` response whose job is linked to
 * the given inquiry — the job detail page's owner control reads it from there.
 * Used for the optimistic update and its error rollback in
 * {@link useUpdateInquiry}.
 */
function patchGetJobAssignee(
  queryClient: QueryClient,
  getJobQueryKey: QueryKey,
  inquiryId: string,
  assignedTo: string,
): void {
  const caches = queryClient.getQueriesData<GetJobResponse>({ queryKey: getJobQueryKey })
  caches.forEach(([queryKey, data]) => {
    const job = data?.job
    if (!job || job.inquiryId !== inquiryId) return
    queryClient.setQueryData(
      queryKey,
      create(GetJobResponseSchema, { job: { ...job, assignedTo: assignedTo || undefined } }),
    )
  })
}

/**
 * Hook to update inquiry data
 *
 * This hook:
 * - Provides mutation function to update inquiry fields
 * - Uses field mask to update only specified fields
 * - Updates cache directly with mutation response (no unnecessary refetches)
 * - Provides helper function specifically for updating stage
 *
 * @returns Mutation result with updateInquiryStage helper and full mutation access
 */
export function useUpdateInquiry() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateInquiry, {
    onMutate: async (variables) => {
      // Cancel list queries to prevent race conditions
      const listQueryKey = createConnectQueryKey({
        schema: listInquiries,
        transport,
        cardinality: undefined,
      })
      await queryClient.cancelQueries({ queryKey: listQueryKey })

      const listCaches = queryClient.getQueriesData<InquiryListCache>({ queryKey: listQueryKey })
      let previousStage: InquiryStageEnum | undefined
      for (const [, data] of listCaches) {
        if (!data) continue
        const pages = 'pages' in data ? data.pages : [data]
        const existing = pages.flatMap((page) => page.inquiries).find((inquiry) => inquiry.id === variables.id)
        if (existing) {
          previousStage = existing.stage
          break
        }
      }

      // Optimistically move the inquiry across ALL list caches
      if (variables.id && variables.updateMask?.paths?.includes('stage') && variables.stage !== undefined) {
        moveInquiryStageInListCaches(queryClient, listQueryKey, variables.id, variables.stage)
      }

      // Handle individual inquiry cache
      const singleQueryKey = createConnectQueryKey({
        schema: getInquiry,
        transport,
        input: { id: variables.id },
        cardinality: 'finite',
      })
      await queryClient.cancelQueries({ queryKey: singleQueryKey })
      const previousSingleData = queryClient.getQueryData<GetInquiryResponse>(singleQueryKey)

      // Optimistically patch the single `GetInquiry` cache too — this is what
      // `useInquiry` (and the Pipeline lead-stage picker built on it) reads,
      // so without this its trigger label/dot stay on the old stage until the
      // request resolves instead of flipping the instant the user picks one.
      if (variables.id && variables.updateMask?.paths?.includes('stage') && variables.stage !== undefined) {
        if (previousSingleData?.inquiry) {
          queryClient.setQueryData(
            singleQueryKey,
            create(GetInquiryResponseSchema, {
              inquiry: { ...previousSingleData.inquiry, stage: variables.stage },
            })
          )
        }
      }

      // Optimistically patch the Pipeline's ListJobs caches too — a job's
      // owner is read from its (derived) assigned_to, not refetched here.
      let previousJobsListCaches: [QueryKey, JobsListCache | undefined][] | undefined
      let jobsListQueryKey: QueryKey | undefined
      if (variables.id && variables.updateMask?.paths?.includes('assigned_to')) {
        jobsListQueryKey = createConnectQueryKey({ schema: listJobs, transport, cardinality: undefined })
        await queryClient.cancelQueries({ queryKey: jobsListQueryKey })
        previousJobsListCaches = queryClient.getQueriesData<JobsListCache>({ queryKey: jobsListQueryKey })
        patchJobAssigneeInListCaches(queryClient, jobsListQueryKey, variables.id, variables.assignedTo ?? '')
      }

      // ...and the job detail page's GetJob, so its owner control flips at once.
      let previousGetJobCaches: [QueryKey, unknown][] | undefined
      if (variables.id && variables.updateMask?.paths?.includes('assigned_to')) {
        const getJobQueryKey = createConnectQueryKey({ schema: getJob, transport, cardinality: undefined })
        await queryClient.cancelQueries({ queryKey: getJobQueryKey })
        previousGetJobCaches = queryClient.getQueriesData({ queryKey: getJobQueryKey })
        patchGetJobAssignee(queryClient, getJobQueryKey, variables.id, variables.assignedTo ?? '')
      }

      return { previousStage, previousSingleData, singleQueryKey, listQueryKey, previousJobsListCaches, jobsListQueryKey, previousGetJobCaches }
    },

    onError: (_error, variables, context) => {
      if (context?.previousStage !== undefined && context.listQueryKey && variables.id) {
        moveInquiryStageInListCaches(queryClient, context.listQueryKey, variables.id, context.previousStage)
      }

      // Rollback single inquiry cache
      if (context?.previousSingleData && context?.singleQueryKey) {
        queryClient.setQueryData(context.singleQueryKey, context.previousSingleData)
      }

      // Rollback the Pipeline's ListJobs caches
      context?.previousJobsListCaches?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })

      // Rollback the job detail's GetJob caches
      context?.previousGetJobCaches?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },

    onSuccess: (response) => {
      const updatedInquiry = response.inquiry
      if (!updatedInquiry) return

      // Update individual inquiry cache
      const queryKey = createConnectQueryKey({
        schema: getInquiry,
        transport,
        input: { id: updatedInquiry.id },
        cardinality: 'finite',
      })
      queryClient.setQueryData(
        queryKey,
        create(GetInquiryResponseSchema, { inquiry: updatedInquiry })
      )

      // Update stage in all list caches (flat + paginated) with server-confirmed value
      const listQueryKey = createConnectQueryKey({
        schema: listInquiries,
        transport,
        cardinality: undefined,
      })
      moveInquiryStageInListCaches(queryClient, listQueryKey, updatedInquiry.id, updatedInquiry.stage)
    },

    onSettled: (_data, _error, variables) => {
      const listQueryKey = createConnectQueryKey({
        schema: listInquiries,
        transport,
        cardinality: undefined,
      })
      void queryClient.invalidateQueries({ queryKey: listQueryKey, refetchType: 'active' })

      // Refetch the dashboard aggregate so its lead cards stay in sync
      void queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getDashboardSummary,
          transport,
          cardinality: undefined,
        }),
        refetchType: 'active',
      })

      // Assignee changes also need to refresh the Pipeline job list — a
      // job's assigned_to is derived from its inquiry's assigned_to, and the
      // Pipeline table reads it from ListJobs, not ListInquiries.
      if (variables?.updateMask?.paths?.includes('assigned_to')) {
        void queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: listJobs, transport, cardinality: undefined }),
          refetchType: 'active',
        })
      }

      // A job's owner and contact are both derived from its inquiry, so the
      // job detail page (GetJob) goes stale on either change. A contact swap
      // also changes the name/company shown on the Pipeline list rows.
      const changedPaths = variables?.updateMask?.paths ?? []
      const jobsAffected = changedPaths.includes('assigned_to') || changedPaths.includes('contact_id')
      if (jobsAffected) {
        void queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: getJob, transport, cardinality: undefined }),
          refetchType: 'active',
        })
      }
      if (changedPaths.includes('contact_id')) {
        void queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: listJobs, transport, cardinality: undefined }),
          refetchType: 'active',
        })
      }

      // Location changes need to refresh the cached drive time — it's keyed
      // on the inquiry's event_location, which just went stale.
      if (variables?.id && variables?.updateMask?.paths?.includes('event_location')) {
        void queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getInquiryDriveTime,
            transport,
            input: { inquiryId: variables.id },
            cardinality: 'finite',
          }),
          refetchType: 'active',
        })
      }
    },
  })

  const updateInquiryStage = async (
    inquiryId: string,
    newStage: InquiryStageEnum,
    rejectReason?: InquiryRejectReasonEnum,
    rejectNotes?: string,
  ) => {
    const paths = ['stage']
    const fields: Record<string, unknown> = {
      id: inquiryId,
      stage: newStage,
    }

    if (rejectReason !== undefined) {
      paths.push('reject_reason')
      fields.rejectReason = rejectReason
    }
    if (rejectNotes !== undefined) {
      paths.push('reject_notes')
      fields.rejectNotes = rejectNotes
    }

    return mutation.mutateAsync(
      create(UpdateInquiryRequestSchema, {
        ...fields,
        updateMask: create(FieldMaskSchema, { paths }),
      })
    )
  }

  const updateInquiryContact = async (inquiryId: string, newContactId: string) => {
    return mutation.mutateAsync(
      create(UpdateInquiryRequestSchema, {
        id: inquiryId,
        contactId: newContactId,
        updateMask: create(FieldMaskSchema, {
          paths: ['contact_id'],
        }),
      })
    )
  }

  const updateInquiryAssignee = async (inquiryId: string, userId: string | null) => {
    return mutation.mutateAsync(
      create(UpdateInquiryRequestSchema, {
        id: inquiryId,
        assignedTo: userId ?? '',
        updateMask: create(FieldMaskSchema, {
          paths: ['assigned_to'],
        }),
      })
    )
  }

  return {
    mutation,
    updateInquiryStage,
    updateInquiryContact,
    updateInquiryAssignee,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useInquiry(inquiryId?: string, options?: { enabled?: boolean }) {
  const query = useQuery(
    getInquiry,
    create(GetInquiryRequestSchema, { id: inquiryId ?? '' }),
    {
      enabled: !!inquiryId && (options?.enabled ?? true),
      select: (data) => data?.inquiry,
    },
  )

  return {
    inquiry: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

export function useGetInquiryDriveTime(inquiryId: string) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session

  const query = useQuery(
    getInquiryDriveTime,
    create(GetInquiryDriveTimeRequestSchema, { inquiryId }),
    {
      enabled: isAuthenticated && !!inquiryId,
    }
  )

  return {
    driveTimeSeconds: query.data?.driveTimeSeconds ?? null,
    distance: query.data?.distance ?? null,
    formattedDuration: query.data?.formattedDuration ?? null,
    isLoading: query.isPending && isAuthenticated && !!inquiryId,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  }
}

/**
 * Hook to create a new inquiry
 *
 * This hook:
 * - Provides mutation function to create inquiry
 * - Sets default stage to INQUIRY_STAGE_NEW
 * - Updates cache with new inquiry
 * - Invalidates list queries to show new inquiry
 *
 * @returns Mutation result with createInquiry function and loading state
 */
export function useCreateInquiry() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createInquiry,
    {
      onSuccess: (response) => {
        const newInquiry = response.inquiry

        if (!newInquiry) return

        // 1. Update individual inquiry cache (getInquiry)
        const queryKey = createConnectQueryKey({
          schema: getInquiry,
          transport,
          input: { id: newInquiry.id },
          cardinality: 'finite',
        })

        // Create a proper GetInquiryResponse message
        queryClient.setQueryData(
          queryKey,
          create(GetInquiryResponseSchema, { inquiry: newInquiry })
        )

        // 2. Invalidate list queries (we can't know all possible filter combinations).
        // Flat (finite) caches — e.g. calendar — eagerly refetch even while inactive.
        // Paginated (infinite) caches (e.g. the lead-source-card's multi-page query)
        // only refetch while actively observed, so an unobserved multi-page query
        // doesn't trigger a serial refetch of every page in the background.
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listInquiries,
            transport,
            cardinality: 'finite',
          }),
          refetchType: 'all',
        })
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listInquiries,
            transport,
            cardinality: 'infinite',
          }),
        })

        // Invalidate the dashboard aggregate so its lead cards refresh too
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getDashboardSummary,
            transport,
            cardinality: undefined,
          }),
          refetchType: 'all',
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Invalidate every cache that can show an inquiry's archived state: the
 * inquiry's own detail cache, all inquiry list caches (both the archived-only
 * and the default variants), the event lists (a cascade archive removes rows
 * from them) and the dashboard aggregate.
 */
function invalidateInquiryArchiveCaches(
  queryClient: QueryClient,
  transport: Transport,
  inquiryId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getInquiry,
      transport,
      input: { id: inquiryId },
      cardinality: 'finite',
    }),
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listInquiries,
      transport,
      cardinality: undefined,
    }),
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listEvents,
      transport,
      cardinality: undefined,
    }),
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getDashboardSummary,
      transport,
      cardinality: undefined,
    }),
    refetchType: 'all',
  })
}

/**
 * Hook to archive (soft-delete) an inquiry.
 *
 * Archiving never cascades to the lead's events by default — the caller opts in
 * per action via `cascadeEvents`.
 */
export function useArchiveInquiry() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(archiveInquiry, {
    onSuccess: (_response, variables) => {
      if (!variables.id) return
      invalidateInquiryArchiveCaches(queryClient, transport, variables.id)
    },
  })

  const archive = async (inquiryId: string, cascadeEvents: boolean) => {
    return mutation.mutateAsync(
      create(ArchiveInquiryRequestSchema, { id: inquiryId, cascadeEvents })
    )
  }

  return {
    archive,
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/** Hook to restore a previously archived inquiry. */
export function useUnarchiveInquiry() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(unarchiveInquiry, {
    onSuccess: (_response, variables) => {
      if (!variables.id) return
      invalidateInquiryArchiveCaches(queryClient, transport, variables.id)
    },
  })

  const unarchive = async (inquiryId: string) => {
    return mutation.mutateAsync(
      create(UnarchiveInquiryRequestSchema, { id: inquiryId })
    )
  }

  return {
    unarchive,
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to generate a portal link for an inquiry
 *
 * This hook:
 * - Generates a shareable portal link with JWT token
 * - Allows customers to view inquiry and quotes without authentication
 * - Requires user to have admin permission on the inquiry's organization
 * - Token expires in 24 hours by default (configurable)
 *
 * @returns Mutation for generating portal link with URL, token, and expiration
 */
export function useGeneratePortalLink() {
  const mutation = useMutation(generatePortalLink)

  return {
    generateLink: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to send inquiry portal links to additional recipients.
 *
 * This is used by quote sharing flows after the quote has been published.
 */
export function useSharePublicInquiryByEmail() {
  const mutation = useMutation(sharePublicInquiryByEmail)

  return {
    shareInquiryByEmail: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to move an accepted inquiry to events
 *
 * This hook:
 * - Creates a booking from the inquiry
 * - Creates events based on eventDays
 * - Copies inquiry items to events
 * - Returns booking ID and event IDs for navigation
 *
 * Prerequisites:
 * - Inquiry must be in ACCEPTED stage
 * - Inquiry must NOT already have a booking
 */
export function useMoveInquiryToEvents() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    moveInquiryToEvents,
    {
      onSuccess: (_response, variables) => {
        // Invalidate inquiry cache to refresh the booking_id and event_ids
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getInquiry,
            transport,
            input: { id: variables.inquiryId },
            cardinality: 'finite',
          }),
        })

        // Invalidate list queries
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listInquiries,
            transport,
            cardinality: undefined,
          }),
        })

        // Invalidate the dashboard aggregate so its lead/event cards refresh too
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getDashboardSummary,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  const moveToEvents = async (inquiryId: string) => {
    return mutation.mutateAsync(
      create(MoveInquiryToEventsRequestSchema, { inquiryId })
    )
  }

  return {
    moveToEvents,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
  }
}

export function useAcceptQuoteAsAdmin() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    acceptQuoteAsAdmin,
    {
      onSuccess: (_response, variables) => {
        // Invalidate single inquiry cache
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getInquiry,
            transport,
            input: { id: variables.inquiryId },
            cardinality: 'finite',
          }),
        })

        // Invalidate inquiry list queries
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listInquiries,
            transport,
            cardinality: undefined,
          }),
        })

        // Invalidate quotes list queries
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listQuotes,
            transport,
            cardinality: undefined,
          }),
        })

        // Invalidate the accepted quote's detail aggregate so its converted-invoice
        // link (GetQuoteDetail.converted_invoice) reflects the newly created invoice.
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getQuoteDetail,
            transport,
            input: { id: variables.quoteId },
            cardinality: 'finite',
          }),
        })

        // Invalidate the dashboard aggregate so its lead cards refresh too
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getDashboardSummary,
            transport,
            cardinality: undefined,
          }),
        })

        // The accepted quote's status, its lite row, and the pipeline job it
        // belongs to (phase, events, activity) all change on acceptance.
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getQuote,
            transport,
            input: { id: variables.quoteId },
            cardinality: 'finite',
          }),
        })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getQuoteLitesByIds, transport, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getJob, transport, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listJobs, transport, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listJobEvents, transport, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listJobActivity, transport, cardinality: undefined }) })
      },
    }
  )

  const acceptQuote = async (inquiryId: string, quoteId: string) => {
    return mutation.mutateAsync(
      create(AcceptQuoteAsAdminRequestSchema, { inquiryId, quoteId })
    )
  }

  return {
    acceptQuote,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
  }
}

/**
 * Hook to prefetch an inquiry on hover
 *
 * This hook provides a prefetch function that can be called on hover
 * to load inquiry data into the cache before the user clicks.
 * Skips prefetching if the data is already in the cache.
 *
 * @returns Prefetch function
 */
export function usePrefetchInquiry() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const prefetchInquiry = async (inquiryId: string) => {
    const queryKey = createConnectQueryKey({
      schema: getInquiry,
      transport,
      input: { id: inquiryId },
      cardinality: 'finite',
    })

    // Skip prefetch if data is already in cache
    const cachedData = queryClient.getQueryData(queryKey)
    if (cachedData) {
      return
    }

    await queryClient.prefetchQuery(
      getInquiryQueryOptions(inquiryId, transport)
    )
  }

  return { prefetchInquiry }
}

/**
 * Invalidates the caches an inquiry event create/update/delete can affect
 * beyond the parent inquiry: the Pipeline job detail page's `ListJobEvents`
 * (an unbooked job's events card mirrors `inquiry.events`) and
 * `GetInquiryDriveTime` (the "Original Inquiry" card's drive time, which can
 * change when an event's location changes). Both are invalidated broadly
 * (no specific id) like `listEvents`/`getDashboardSummary` above — cheap when
 * inactive, and this call site doesn't have the job id to scope it further.
 */
function invalidateInquiryEventJobCaches(queryClient: QueryClient, transport: Transport) {
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listJobEvents,
      transport,
      cardinality: undefined,
    }),
    refetchType: 'all',
  })
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getInquiryDriveTime,
      transport,
      cardinality: undefined,
    }),
    refetchType: 'all',
  })
}

/**
 * Hook to create a new inquiry event
 *
 * This hook:
 * - Creates a new event for an inquiry
 * - Invalidates the inquiry cache to refresh the events array
 *
 * @returns Mutation for creating inquiry event
 */
export function useCreateInquiryEvent() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(createInquiryEvent, {
    onSuccess: (_response, variables) => {
      // Invalidate the inquiry cache to refresh events
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getInquiry,
          transport,
          input: { id: variables.inquiryId },
          cardinality: 'finite',
        }),
      })
      // Also invalidate listEvents so the calendar refetches when navigated to.
      // refetchType: 'all' ensures inactive queries (e.g. calendar) are also refetched.
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEvents,
          transport,
          cardinality: undefined,
        }),
        refetchType: 'all',
      })
      // Invalidate the dashboard aggregate so its event cards refresh too
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getDashboardSummary,
          transport,
          cardinality: undefined,
        }),
        refetchType: 'all',
      })
      invalidateInquiryEventJobCaches(queryClient, transport)
    },
  })

  const createEvent = async (params: {
    inquiryId: string
    eventDate?: Timestamp
    eventLength?: number
    eventGuests?: number
    baristaCount?: number
    cartCount?: number
    eventTimezone?: string
    eventType?: string
    eventLocation?: string
  }) => {
    return mutation.mutateAsync(
      create(CreateInquiryEventRequestSchema, {
        ...params,
        eventTypeKey: params.eventType,
        eventType: undefined,
      })
    )
  }

  return {
    createEvent,
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to update an inquiry event
 *
 * This hook:
 * - Updates an existing inquiry event
 * - Uses field mask for partial updates
 * - Invalidates the parent inquiry cache to refresh events
 *
 * @param inquiryId - The parent inquiry ID for cache invalidation
 * @returns Mutation for updating inquiry event
 */
export function useUpdateInquiryEvent(inquiryId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateInquiryEvent, {
    onSuccess: () => {
      // Invalidate the inquiry cache to refresh events
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getInquiry,
          transport,
          input: { id: inquiryId },
          cardinality: 'finite',
        }),
      })
      invalidateInquiryEventJobCaches(queryClient, transport)
    },
  })

  const updateEvent = async (params: {
    id: string
    eventDate?: Timestamp
    eventLength?: number
    eventGuests?: number
    baristaCount?: number
    cartCount?: number
    eventTimezone?: string
    eventType?: string
    eventLocation?: string
    updateMask?: string[]
  }) => {
    const { updateMask, eventType, ...rest } = params
		return mutation.mutateAsync(
      create(UpdateInquiryEventRequestSchema, {
        ...rest,
			eventTypeKey: eventType,
			updateMask: updateMask ? create(FieldMaskSchema, {
				paths: updateMask.map((path) => path === 'event_type' ? 'event_type_key' : path),
			}) : undefined,
      })
    )
  }

  return {
    updateEvent,
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to delete an inquiry event
 *
 * This hook:
 * - Deletes an inquiry event
 * - Backend prevents deleting the last event (returns error)
 * - Invalidates the parent inquiry cache to refresh events
 *
 * @param inquiryId - The parent inquiry ID for cache invalidation
 * @returns Mutation for deleting inquiry event
 */
export function useDeleteInquiryEvent(inquiryId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(deleteInquiryEvent, {
    onSuccess: () => {
      // Invalidate the inquiry cache to refresh events
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getInquiry,
          transport,
          input: { id: inquiryId },
          cardinality: 'finite',
        }),
      })
      invalidateInquiryEventJobCaches(queryClient, transport)
    },
  })

  const deleteEvent = async (eventId: string) => {
    return mutation.mutateAsync(
      create(DeleteInquiryEventRequestSchema, { id: eventId })
    )
  }

  return {
    deleteEvent,
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}
